import Vue from 'vue'
import { HTTP_API_V2 } from '../../http-common'
import { isStale } from '../../utils/aeiFreshness'

// AEI devices and their latest state.
//
// The latest state comes from the Firebase Realtime Database
// (status/aei_integra/<gateway>/devices/<deviceKey>), kept current by the
// ingest; the telemetry database is only queried for history. See
// src/mixins/aeiRealtimeMixin.js for the subscriptions that fill
// `realtimeByDevice`.

const AEI_HOUSE_MODEL = 'aei_integra'
const AEI_PROCESSING_MODEL = 'aei_integra_processing'

// Flattens one record type of a realtime node into reading rows:
//   records/<resource>/metrics/<metric>            -> scalar
//   records/<resource>/metrics/<metric>/idx_<n>    -> indexed
//
// Record types are keyed by the Platform's resource name (`feed_bin`), the same
// name their telemetry table carries; AEI's export codes stay on the wire
// between the agent and the ingest.
function readingsFromRecord (record) {
  const metrics = (record && record.metrics) || {}
  const rows = []
  Object.keys(metrics).forEach(metric => {
    const entry = metrics[metric]
    if (entry && Object.prototype.hasOwnProperty.call(entry, 'value')) {
      rows.push(toReading(metric, null, entry))
      return
    }
    Object.keys(entry || {})
      .filter(key => key.indexOf('idx_') === 0)
      .forEach(key => rows.push(toReading(metric, Number(key.slice(4)), entry[key])))
  })
  return rows.sort((a, b) => a.metric.localeCompare(b.metric) || (a.idx || 0) - (b.idx || 0))
}

function toReading (metric, idx, leaf) {
  const isNumber = typeof leaf.value === 'number'
  return {
    metric,
    idx,
    value_num: isNumber ? leaf.value : null,
    value_text: isNumber ? null : leaf.value,
    mu: leaf.mu || null,
    rec_at: leaf.recAt || null,
    updatedAt: typeof leaf.updatedAt === 'number' ? leaf.updatedAt : null,
    // Feed bins only: heaviest weight ever reported, used as estimated capacity.
    observedMax: typeof leaf.observedMax === 'number' ? leaf.observedMax : null
  }
}

export default {
  state: {
    loadedAeiHouses: [],
    aeiHousesLoaded: false,
    // Realtime node per device id, as last received from Firebase.
    realtimeByDevice: {},
    // `lastActivityAt` per gateway key: when we last accepted an upload from it.
    gatewayActivity: {},
    aeiLoading: false
  },
  getters: {
    loadedAeiHouses (state) {
      return state.loadedAeiHouses
    },
    aeiHousesLoaded (state) {
      return state.aeiHousesLoaded
    },
    aeiLoading (state) {
      return state.aeiLoading
    },
    // Houses only; processing machines are a separate section of the UI.
    aeiHouseDevices (state) {
      return state.loadedAeiHouses.filter(house => house.model === AEI_HOUSE_MODEL)
    },
    aeiProcessingDevices (state) {
      return state.loadedAeiHouses.filter(house => house.model === AEI_PROCESSING_MODEL)
    },
    // Devices that report data and carry a plan. The list also holds the
    // gateway, so a customer is recognised as AEI before its first upload; the
    // gateway has no plan (hasActivePlan is null) and reports nothing itself.
    aeiReportingDevices (state) {
      return state.loadedAeiHouses.filter(house =>
        house.model === AEI_HOUSE_MODEL || house.model === AEI_PROCESSING_MODEL)
    },
    // AEI devices are recognised by model, not by the brand of their plan: a
    // device whose AEI plan was revoked has no subscription, yet it is still an
    // AEI device and its owner must keep the AEI experience.
    aeiDeviceIds (state) {
      return state.loadedAeiHouses.map(house => house.deviceId)
    },
    hasAeiFeature (state, getters) {
      return getters.aeiDeviceIds.length > 0
    },
    aeiDevicesWithoutPlan (state) {
      return state.loadedAeiHouses.filter(house => house.hasActivePlan === false)
    },
    // True when every one of the user's devices is AEI, which swaps the shell.
    //
    // Inobram devices always hold a subscription (free at least), so they
    // always appear in the feature list; AEI devices appear only while their
    // plan is active. A feature-list device that is not in the AEI list is
    // therefore an Inobram device, and a mixed customer keeps the Inobram
    // navigation. Nothing is decided until both lists have loaded, so the shell
    // never flips back and forth while they arrive.
    isAeiOnlyUser (state, getters, rootState, rootGetters) {
      if (!state.aeiHousesLoaded || !rootGetters.deviceFeaturesLoaded || !getters.hasAeiFeature) {
        return false
      }
      const aeiIds = new Set(getters.aeiDeviceIds)
      return rootGetters.loadedDeviceFeaturesV2.every(device => aeiIds.has(device.deviceId))
    },
    aeiLatestReadings: (state) => (deviceId, recordType) => {
      const node = state.realtimeByDevice[deviceId]
      const record = node && node.records && node.records[recordType]
      return readingsFromRecord(record)
    },
    // Latest numeric value of one metric ({ value, mu }), or null.
    aeiMetric: (state, getters) => (deviceId, recordType, metric, idx = null) => {
      const reading = getters.aeiLatestReadings(deviceId, recordType)
        .find(row => row.metric === metric && row.idx === idx && row.value_num !== null)
      return reading ? { value: reading.value_num, mu: reading.mu } : null
    },
    // Sum of every value of a metric (indexed meters such as water_use), or null.
    aeiMetricTotal: (state, getters) => (deviceId, recordType, metric) => {
      const values = getters.aeiLatestReadings(deviceId, recordType)
        .filter(row => row.metric === metric && row.value_num !== null)
        .map(row => row.value_num)
      return values.length ? values.reduce((total, value) => total + value, 0) : null
    },
    // Record types a device has reported, e.g. the one a processing machine sends.
    aeiRecordTypes: (state) => (deviceId) => {
      const node = state.realtimeByDevice[deviceId]
      return Object.keys((node && node.records) || {})
    },
    // When a gateway last uploaded, epoch ms, or null before its first one.
    //
    // Written per accepted batch, so it moves even when a batch carried nothing
    // new — an agent that is running and a farm whose values are simply not
    // changing are told apart here, and nowhere else.
    aeiGatewayLastActivityAt: (state) => (gatewayKey) => {
      const value = state.gatewayActivity[gatewayKey]
      return typeof value === 'number' ? value : null
    },
    // Newest update of any record type of a device, epoch ms, or null: how
    // fresh the card as a whole is, whatever it happens to show.
    aeiDeviceUpdatedAt: (state) => (deviceId) => {
      const node = state.realtimeByDevice[deviceId]
      const records = (node && node.records) || {}
      const times = Object.keys(records)
        .map(recordType => records[recordType] && records[recordType].updatedAt)
        .filter(value => typeof value === 'number')
      return times.length ? Math.max(...times) : null
    },
    // Newest update of a record type for a device, epoch ms, or null.
    aeiRecordUpdatedAt: (state) => (deviceId, recordType) => {
      const node = state.realtimeByDevice[deviceId]
      const record = node && node.records && node.records[recordType]
      return record && typeof record.updatedAt === 'number' ? record.updatedAt : null
    },
    // Feed bins for a house, ordered by bin number, each with its own freshness.
    aeiFeedBins: (state, getters) => (deviceId) => {
      return getters.aeiLatestReadings(deviceId, 'feed_bin')
        .filter(reading => reading.metric === 'feed_weight' && reading.idx !== null)
        .map(reading => ({
          binNumber: reading.idx,
          value: reading.value_num,
          unit: reading.mu || 'lb',
          observedMax: reading.observedMax,
          recordedAt: reading.updatedAt ? new Date(reading.updatedAt) : null,
          isStale: isStale(reading.updatedAt)
        }))
        .sort((a, b) => a.binNumber - b.binNumber)
    },
    aeiHouseTotalFeed: (state, getters) => (deviceId) => {
      return getters.aeiFeedBins(deviceId)
        .reduce((total, bin) => total + (Number(bin.value) || 0), 0)
    }
  },
  mutations: {
    setLoadedAeiHouses (state, payload) {
      state.loadedAeiHouses = payload
      state.aeiHousesLoaded = true
    },
    setAeiRealtimeNode (state, { deviceId, node }) {
      Vue.set(state.realtimeByDevice, deviceId, node || null)
    },
    setAeiGatewayActivity (state, { gatewayKey, lastActivityAt }) {
      Vue.set(state.gatewayActivity, gatewayKey, lastActivityAt)
    },
    setAeiLoading (state, payload) {
      state.aeiLoading = payload
    },
    clearAei (state) {
      state.loadedAeiHouses = []
      state.aeiHousesLoaded = false
      state.realtimeByDevice = {}
      state.gatewayActivity = {}
    }
  },
  actions: {
    loadAeiHouses ({ commit }) {
      commit('setAeiLoading', true)
      return HTTP_API_V2.get('aei/houses')
        .then(result => {
          const houses = Array.isArray(result.data) ? result.data : []
          commit('setLoadedAeiHouses', houses)
          return houses
        })
        .catch(err => {
          // Stays unloaded, so the shell is not swapped on a failed request.
          console.log('error loading AEI houses: ', err)
          return []
        })
        .finally(() => {
          commit('setAeiLoading', false)
        })
    },
    // History for charts and reports; the latest state comes from the realtime database.
    loadAeiTelemetryHistory (context, { deviceId, recordType, metrics, initialDateTime, finalDateTime, limit }) {
      const params = {}
      if (metrics && metrics.length) params.metrics = metrics.join(',')
      if (initialDateTime) params.initialDateTime = initialDateTime
      if (finalDateTime) params.finalDateTime = finalDateTime
      if (limit) params.limit = limit

      return HTTP_API_V2.get(`aei/telemetry/${deviceId}/${recordType}`, { params })
        .then(result => (Array.isArray(result.data) ? result.data : []))
        .catch(err => {
          console.log(`error loading AEI ${recordType} history: `, err)
          return []
        })
    }
  }
}



// WEBPACK FOOTER //
// ./src/store/aei/index.js